/**
 * Planner Config Admin
 *
 * Accordion-based editor for wizard configuration.
 * Expand sections to see/edit questions inline.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import PlannerConfigService from '../../../services/plannerConfigService';
import SectionAccordion from './SectionAccordion';
// DemoPreviewModal no longer used - demo opens in new tab at /admin/planner-demo
import { toast } from 'react-hot-toast';
import { Save, RotateCcw, Database, FileJson, AlertTriangle, Play } from 'lucide-react';

const PlannerConfigAdmin = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [validationErrors, setValidationErrors] = useState([]);
  const [versionInfo, setVersionInfo] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    loadConfig();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      PlannerConfigService.invalidateCache();
      const data = await PlannerConfigService.loadConfiguration();

      // Check if still mounted before updating state
      if (!mountedRef.current) return;

      const { _versionId, _fromDatabase, ...configData } = data;
      setConfig(configData);
      setOriginalConfig(JSON.stringify(configData));
      setVersionInfo({ versionId: _versionId, fromDatabase: _fromDatabase });
      setValidationErrors([]);
    } catch (error) {
      if (!mountedRef.current) return;
      console.error('Error loading config:', error);
      toast.error('Failed to load configuration');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  const hasChanges = config && JSON.stringify(config) !== originalConfig;

  const handleSave = async () => {
    // Prevent double-clicks
    if (saving) return;

    const errors = PlannerConfigService.validateConfig(config);
    setValidationErrors(errors);

    if (errors.length > 0) {
      toast.error(`${errors.length} validation error(s) - fix before saving`);
      return;
    }

    setSaving(true);
    try {
      const { error } = await PlannerConfigService.saveConfiguration(
        config,
        user?.id,
        'Updated via config admin'
      );

      if (!mountedRef.current) return;

      if (error) {
        toast.error('Failed to save: ' + (error.message || 'Unknown error'));
        return;
      }

      toast.success('Saved');
      await loadConfig();
    } catch (err) {
      if (!mountedRef.current) return;
      console.error('Error saving:', err);
      toast.error('Failed to save');
    } finally {
      if (mountedRef.current) {
        setSaving(false);
      }
    }
  };

  const handleReset = () => {
    if (originalConfig) {
      setConfig(JSON.parse(originalConfig));
      setValidationErrors([]);
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };

  const handleUpdateQuestion = useCallback((sectionKey, questionId, updates) => {
    setConfig(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...prev.sections[sectionKey],
          questions: {
            ...prev.sections[sectionKey].questions,
            [questionId]: {
              ...prev.sections[sectionKey].questions[questionId],
              ...updates
            }
          }
        }
      }
    }));
  }, []);

  const handleUpdateSection = useCallback((sectionKey, updates) => {
    setConfig(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...prev.sections[sectionKey],
          ...updates
        }
      }
    }));
  }, []);

  // Get all question IDs for navigation dropdowns
  const allQuestionIds = config ?
    Object.values(config.sections).flatMap(s => Object.keys(s.questions || {})) : [];

  if (loading) {
    return (
      <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--gray-400)' }}>
        Loading configuration...
      </div>
    );
  }

  const sectionEntries = Object.entries(config?.sections || {});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        padding: 'var(--space-4)',
        borderBottom: '1px solid var(--gray-700)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        flexWrap: 'wrap'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 'var(--text-lg)', color: 'var(--gray-50)' }}>
            Model Planner Configuration
          </h2>
          <div style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--gray-500)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-1)'
          }}>
            {versionInfo?.fromDatabase ? (
              <>
                <Database size={12} />
                <span>v{versionInfo.versionId?.slice(0, 8)}</span>
              </>
            ) : (
              <>
                <FileJson size={12} />
                <span>JSON fallback</span>
              </>
            )}
            <span style={{ color: 'var(--gray-600)' }}>•</span>
            <span>{sectionEntries.length} sections</span>
            <span style={{ color: 'var(--gray-600)' }}>•</span>
            <span>{allQuestionIds.length} questions</span>
            {hasChanges && (
              <span style={{ color: 'var(--amber-400)', marginLeft: 'var(--space-2)' }}>
                • unsaved changes
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            onClick={() => window.open('/planner-demo', '_blank')}
            aria-label="Open demo in new tab"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3)',
              background: 'var(--cta-blue)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontSize: 'var(--text-sm)'
            }}
          >
            <Play size={14} />
            Demo
          </button>
          <button
            onClick={handleReset}
            disabled={!hasChanges}
            aria-label="Reset changes"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3)',
              background: 'var(--gray-700)',
              color: hasChanges ? 'var(--gray-300)' : 'var(--gray-500)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: hasChanges ? 'pointer' : 'not-allowed',
              fontSize: 'var(--text-sm)'
            }}
          >
            <RotateCcw size={14} />
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            aria-label="Save configuration"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3)',
              background: hasChanges && !saving ? 'var(--green-600)' : 'var(--gray-600)',
              color: hasChanges && !saving ? 'white' : 'var(--gray-400)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
              fontSize: 'var(--text-sm)'
            }}
          >
            <Save size={14} />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div
          role="alert"
          style={{
            padding: 'var(--space-3) var(--space-4)',
            background: 'rgba(239, 68, 68, 0.1)',
            borderBottom: '1px solid rgba(239, 68, 68, 0.3)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-2)'
          }}
        >
          <AlertTriangle size={16} style={{ color: 'var(--red-400)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--red-300)' }}>
            <strong>{validationErrors.length} error{validationErrors.length !== 1 ? 's' : ''}:</strong>
            {' '}{validationErrors.slice(0, 3).map(e => e.message).join('; ')}
            {validationErrors.length > 3 && ` (+${validationErrors.length - 3} more)`}
          </div>
        </div>
      )}

      {/* Section List */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-4)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {sectionEntries.map(([sectionKey, section]) => (
            <SectionAccordion
              key={sectionKey}
              sectionKey={sectionKey}
              section={section}
              expanded={expandedSections.has(sectionKey)}
              onToggle={() => toggleSection(sectionKey)}
              onUpdateQuestion={handleUpdateQuestion}
              onUpdateSection={handleUpdateSection}
              allQuestionIds={allQuestionIds}
              validationErrors={validationErrors.filter(e => e.section === sectionKey)}
            />
          ))}
        </div>
      </div>

    </div>
  );
};

export default PlannerConfigAdmin;
